import React from 'react';
import { ArcSocketContext } from '@src/core/socket';
import { useMatch, useSearchParams } from 'react-router-dom';
import { routesConfig } from '@src/core/routes';
import { PointEntity } from '@src/features/order/entities';

const useGoTo = () => {
  const [searchParams] = useSearchParams();
  const match = useMatch(`${routesConfig.drones}`);

  const { arcSocket } = React.useContext(ArcSocketContext);
  
  const [pointsByDrone, setPointsByDrone] = React.useState<Record<string, PointEntity[]>>({});
  const [positionByDrone, setPositionByDrone] = React.useState<Record<string, google.maps.LatLngLiteral | null>>({});

  const droneSerial = searchParams.get('droneSerial');

  // Get current points and position for the selected drone
  const points = React.useMemo(() => {
    return droneSerial ? (pointsByDrone[droneSerial] ?? []) : [];
  }, [droneSerial, pointsByDrone]);

  const position = React.useMemo(() => {
    return droneSerial ? (positionByDrone[droneSerial] ?? null) : null;
  }, [droneSerial, positionByDrone]);

  const removePosition = React.useCallback(() => {
    if (!droneSerial) return;
    setPositionByDrone(prev => ({
      ...prev,
      [droneSerial]: null,
    }));
  }, [droneSerial]);

  const addPosition = React.useCallback(async (e: google.maps.LatLngLiteral) => {
    if (!droneSerial) return;
    setPositionByDrone(prev => ({
      ...prev,
      [droneSerial]: e,
    }));
  }, [droneSerial]);

  const resetGoHere = React.useCallback(() => {
    if (!droneSerial) return;
    setPointsByDrone(prev => {
      const updated = { ...prev };
      delete updated[droneSerial];
      return updated;
    });
    setPositionByDrone(prev => ({
      ...prev,
      [droneSerial]: null,
    }));
  }, [droneSerial]);

  const goTo = React.useCallback((e: google.maps.LatLngLiteral) => {
    const socket = arcSocket();
    const serialNumber = searchParams.get('droneSerial');
    
    if (!socket || !match || !serialNumber) return;

    const point: PointEntity = {
      id: Date.now(),
      position: { 
        lat: e.lat, 
        lng: e.lng, 
      }
    };
    
    setPointsByDrone(prev => ({
      ...prev,
      [serialNumber]: [point],
    }));
    
    socket.send(
      JSON.stringify({
        serialNumber,
        type: "COMMAND",
        command: "GOTO",
        latitude_deg: e.lat,
        longitude_deg: e.lng,
      })
    );
  }, [arcSocket, match, searchParams]);
 
  return {
    goTo,
    points, 
    position,
    resetGoHere,
    addPosition,
    removePosition,
  };
};

export default useGoTo;